import { EmptyState } from "~/components/screens/StateScreen"
import { HistoryIcon, RotateCcwIcon } from "lucide-react"
import { type ReactNode, useState } from "react"
import { toast } from "sonner"
import { formatDate } from "~/lib/utils"
import { api } from "~/trpc/react"
import { Button } from "../ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "../ui/dialog"
import { ScrollArea } from "../ui/scroll-area"

export function KBHistoryDialog({
	teamId,
	documentId,
	onRestored,
	children
}: {
	teamId: string
	documentId: string
	onRestored?: (slug?: string) => void
	children: ReactNode
}) {
	const [open, setOpen] = useState(false)
	const utils = api.useUtils()
	const history = api.knowledgeBase.history.useQuery(
		{ teamId, documentId },
		{ enabled: open }
	)
	const current = api.knowledgeBase.get.useQuery(
		{ teamId, documentId },
		{ enabled: open }
	)
	const restore = api.knowledgeBase.restoreHistory.useMutation({
		onError: (error) => toast.error(error.message),
		onSuccess: async (document) => {
			await utils.knowledgeBase.get.invalidate({ teamId, documentId })
			await utils.knowledgeBase.list.invalidate({ teamId })
			await utils.knowledgeBase.history.invalidate({ teamId, documentId })
			toast("Document restored")
			onRestored?.(document?.slug)
			setOpen(false)
		}
	})

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent size="md">
				<DialogHeader>
					<DialogTitle>Document history</DialogTitle>
					<DialogDescription>
						Restore an older saved state of this knowledge base document.
					</DialogDescription>
				</DialogHeader>
				<ScrollArea className="max-h-96 pr-3">
					<div className="flex flex-col gap-2">
						{history.data?.length ? (
							history.data.map((entry) => (
								<div
									key={entry.id}
									className="flex items-start justify-between gap-3 rounded-lg border border-stroke bg-background-secondary p-3"
								>
									<div className="min-w-0">
										<div className="flex items-center gap-2 text-sm font-medium text-text-main">
											<HistoryIcon className="size-4" />
											<span className="truncate">{entry.title}</span>
										</div>
										<p className="mt-1 text-xs text-text-small">
											Saved {formatDate(entry.createdAt)}
											{entry.actor?.orgMember.user.name
												? ` by ${entry.actor.orgMember.user.name}`
												: ""}
										</p>
										{entry.description ? (
											<p className="mt-2 line-clamp-2 text-sm text-text-sub">
												{entry.description}
											</p>
										) : null}
									</div>
									<Button
										variant="outline"
										size="sm"
										disabled={restore.isPending || !current.data}
										onClick={() =>
											restore.mutate({
												teamId,
												documentId,
												historyId: entry.id,
												contentRevision: current.data!.contentRevision,
												updatedAt: current.data!.updatedAt
											})
										}
									>
										<RotateCcwIcon className="size-4" />
										Restore
									</Button>
								</div>
							))
						) : (
							<EmptyState scope="inline" title="No saved history yet." />
						)}
					</div>
				</ScrollArea>
			</DialogContent>
		</Dialog>
	)
}
